// src/components/EditEntryModal.modern.tsx
/**
 * Modernized Edit Expense Modal
 * - Clean, minimal design with better visual hierarchy
 * - Progressive disclosure (GST toggle)
 * - Improved error handling and validation
 * - Better mobile responsiveness
 * - Sticky header/footer for long forms
 */

import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { X, AlertCircle, CheckCircle, Info } from "lucide-react";
import { useAuthContext } from "../context/AuthContext";
import { db } from "../firebase";
import { useTitleCase } from "../hooks/useTitleCase";
import { calculateAmount } from "../utils/calculateAmount";
import { auditLogger } from "../services/auditLogger";
import GroupedCategoryDropdown from "./forms/GroupedCategoryDropdown";
import Button from '../ui/Button';

type TypeOption = { id: string; name: string };
type UnitOption = { id: string; name: string; type: string };
type CostCenterOption = { id: string; name: string; type: string };
type CategoryOption = {
  id: string;
  name: string;
  type: string;
  costCenter: string;
  needsNameField?: boolean;
  requiresNameField?: boolean; // Legacy support
  supportsUnits?: boolean;
};

interface EditEntryModalProps {
  open: boolean;
  entry: any;
  onClose: () => void;
  onSaved: () => void;
  types: TypeOption[];
  units: UnitOption[];
  costCenters: CostCenterOption[];
  categories: CategoryOption[];
  userEmail: string;
}

const FIVE_DAY_MS = 365 * 24 * 60 * 60 * 1000;

const EditEntryModal: React.FC<EditEntryModalProps> = ({
  open,
  entry,
  onClose,
  onSaved,
  types,
  units,
  costCenters,
  categories,
  userEmail,
}) => {
  const { roles, assignedUnits } = useAuthContext();
  const toTitleCase = useTitleCase();
  
  // Form state
  const [formData, setFormData] = useState({
    type: "",
    unit: "",
    costCenter: "",
    category: "",
    shop: "",
    date: new Date().toISOString().split("T")[0],
    mode: "total" as "total" | "unit",
    gross: "",
    quantity: "",
    rate: "",
    gstAmount: "",
    enableGst: false,
    amount: "",
    description: "",
    name: "",
  });

  // UI state
  const [shopList, setShopList] = useState<string[]>([]);
  const [showName, setShowName] = useState(false);
  const [showUnits, setShowUnits] = useState(false);
  const [legacyWarning, setLegacyWarning] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch shops
  useEffect(() => {
    const fetchShops = async () => {
      const snap = await getDocs(collection(db, "shops"));
      const shops = snap.docs
        .map(d => d.data().name as string)
        .filter(Boolean)
        .map(toTitleCase)
        .sort();
      setShopList(shops);
    };
    fetchShops();
  }, [toTitleCase]);

  // Initialize form when entry changes
  useEffect(() => {
    if (!open || !entry) return;

    const catId = entry.categoryId || entry.category;
    const cat = categories.find(c => c.id === catId);
    const reqName = !!(cat?.needsNameField || cat?.requiresNameField);
    const supports = cat?.supportsUnits ?? false;
    
    setShowName(reqName);
    setShowUnits(supports);
    setLegacyWarning(!cat);

    setFormData({
      type: entry.typeId || entry.type || "",
      unit: entry.unitId || entry.unit || "",
      costCenter: entry.costCenterId || entry.costCenter || "",
      category: catId || "",
      shop: entry.shop || "",
      date: entry.date && typeof entry.date === "string"
        ? entry.date.slice(0, 10)
        : entry.date?.toISOString?.()
        ? entry.date.toISOString().slice(0, 10)
        : new Date().toISOString().split("T")[0],
      mode: entry.mode || (entry.quantity ? "unit" : "total"),
      gross: entry.grossAmount?.toString() || entry.gross?.toString() || "",
      quantity: entry.quantity?.toString() || "",
      rate: entry.rate?.toString() || "",
      gstAmount: entry.gstAmount?.toString() || entry.taxAmount?.toString() || "",
      enableGst: (entry.gstAmount > 0 || entry.taxAmount > 0) || false,
      amount: entry.amount?.toString() || "",
      description: entry.description || "",
      name: entry.name || "",
    });

    setError(null);
    setErrors({});
    setSuccessMessage(null);
  }, [entry, open, categories]);

  // Auto-calculate amount
  useEffect(() => {
    const computed = calculateAmount({
      mode: formData.mode,
      quantity: formData.quantity,
      rate: formData.rate,
      gross: formData.gross,
      gstAmount: formData.enableGst ? formData.gstAmount : "0",
      enableGst: formData.enableGst,
      supportsUnits: showUnits,
    });
    setFormData(prev => ({ ...prev, amount: computed ? String(computed) : "" }));
  }, [
    formData.mode,
    formData.quantity,
    formData.rate,
    formData.gross,
    formData.gstAmount,
    formData.enableGst,
    showUnits,
  ]);

  // Update showName/showUnits when category changes
  useEffect(() => {
    const cat = categories.find(c => c.id === formData.category);
    setShowName(!!(cat?.needsNameField || cat?.requiresNameField));
    setShowUnits(!!cat?.supportsUnits);

    // Auto-set mode based on category
    if (cat?.supportsUnits && formData.mode === "total") {
      setFormData(prev => ({ ...prev, mode: "unit" }));
    }
  }, [formData.category, categories]);

  // Filtered dropdowns
  const filteredUnits = units.filter(u => {
    const matchesType = !formData.type || u.type === formData.type;
    const hasAccess = roles.includes("admin") || assignedUnits.includes(u.id);
    return matchesType && hasAccess;
  });

  const filteredCostCenters = costCenters.filter(c => {
    return !formData.type || c.type === formData.type;
  });

  const filteredCategories = categories.filter(c => {
    return c.type === formData.type && c.costCenter === formData.costCenter;
  });

  // Validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.type) newErrors.type = "Type is required";
    if (!formData.unit) newErrors.unit = "Unit is required";
    if (!formData.costCenter) newErrors.costCenter = "Cost Center is required";
    if (!formData.category) newErrors.category = "Category is required";
    if (!formData.shop) newErrors.shop = "Shop is required";
    if (!formData.date) newErrors.date = "Date is required";

    // Date validation
    if (formData.date && new Date(formData.date) > new Date()) {
      newErrors.date = "Date cannot be in the future";
    }

    // Mode-specific validation
    if (formData.mode === "total") {
      if (!formData.gross) newErrors.gross = "Gross amount is required";
    } else {
      if (!formData.quantity) newErrors.quantity = "Quantity is required";
      if (!formData.rate) newErrors.rate = "Rate is required";
    }

    // Name field validation if required
    if (showName && !formData.name) {
      const cat = categories.find(c => c.id === formData.category);
      newErrors.name = `${cat?.name || "Item"} name is required`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit handler
  const handleSave = async () => {
    if (!validateForm()) return;

    setError(null);
    setLoading(true);

    // Permission checks
    if (!roles.includes("admin")) {
      const created =
        entry.createdAt && typeof entry.createdAt.toDate === "function"
          ? entry.createdAt.toDate()
          : new Date(entry.createdAt || entry.date);
      if (Date.now() - created.getTime() > FIVE_DAY_MS) {
        setError("You can only edit entries from the last 365 days.");
        setLoading(false);
        return;
      }
      if (!assignedUnits.includes(formData.unit)) {
        setError("You do not have access to this unit.");
        setLoading(false);
        return;
      }
    }

    try {
      // Capture before state
      const beforeData = {
        typeId: entry.typeId,
        type: entry.type,
        unitId: entry.unitId,
        unit: entry.unit,
        costCenterId: entry.costCenterId,
        costCenter: entry.costCenter,
        categoryId: entry.categoryId,
        category: entry.category,
        shop: entry.shop,
        amount: entry.amount,
        grossAmount: entry.grossAmount,
        quantity: entry.quantity,
        rate: entry.rate,
        gstAmount: entry.gstAmount,
        mode: entry.mode,
        date: entry.date,
        description: entry.description,
        name: entry.name
      };

      const amount = calculateAmount({
        mode: formData.mode,
        quantity: formData.quantity,
        rate: formData.rate,
        gross: formData.gross,
        gstAmount: formData.enableGst ? formData.gstAmount : "0",
        enableGst: formData.enableGst,
        supportsUnits: showUnits,
      });

      const updatePayload = {
        typeId: formData.type,
        type: types.find(t => t.id === formData.type)?.name || "",
        unitId: formData.unit,
        unit: units.find(u => u.id === formData.unit)?.name || "",
        costCenterId: formData.costCenter,
        costCenter: costCenters.find(c => c.id === formData.costCenter)?.name || "",
        categoryId: formData.category,
        category: categories.find(c => c.id === formData.category)?.name || "",
        shop: toTitleCase(formData.shop || ""),
        amount,
        ...(formData.mode === "total"
          ? { grossAmount: Number(formData.gross || 0) }
          : {
              quantity: Number(formData.quantity || 0),
              rate: Number(formData.rate || 0),
            }),
        ...(formData.enableGst
          ? { gstAmount: Number(formData.gstAmount) }
          : { gstAmount: 0 }),
        mode: formData.mode,
        date: new Date(formData.date),
        description: toTitleCase(formData.description || ""),
        ...(showName ? { name: toTitleCase(formData.name || "") } : { name: "" }),
        updatedAt: serverTimestamp(),
        updatedBy: userEmail,
      };

      await updateDoc(doc(db, "expenses", entry.id), updatePayload);

      // Audit log
      await auditLogger.logExpenseUpdate(
        entry.id,
        beforeData,
        { ...updatePayload, updatedAt: new Date() },
        {
          customMetadata: {
            editedBy: userEmail,
            editSource: 'EditEntryModal'
          }
        }
      );

      setSuccessMessage("Expense updated successfully!");
      setTimeout(() => {
        onSaved();
        onClose();
      }, 800);
    } catch (err: any) {
      console.error('Error updating expense:', err);
      setError(err.message || "Error updating entry");
    }
    setLoading(false);
  };

  if (!open) return null;

  const todayStr = new Date().toISOString().split("T")[0];
  const minDateStr = roles.includes("admin")
    ? ""
    : new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  const selectedCategory = categories.find(c => c.id === formData.category);

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-200">
          
          {/* Header - Sticky */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Edit Expense Entry
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Update expense details • Entry ID: {entry?.id?.slice(0, 8)}...
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Scrollable Form Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="space-y-6">
              
              {/* Alerts */}
              {error && (
                <div className="p-4 bg-error-50 border border-error-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-error-800">Error</p>
                    <p className="text-sm text-error-600 mt-1">{error}</p>
                  </div>
                </div>
              )}

              {successMessage && (
                <div className="p-4 bg-success-50 border border-success-200 rounded-lg flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-success-800">{successMessage}</p>
                  </div>
                </div>
              )}

              {legacyWarning && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                  <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800">Legacy Entry</p>
                    <p className="text-sm text-amber-600 mt-1">Category could not be resolved from master data</p>
                  </div>
                </div>
              )}

              {/* Section 1: Expense Hierarchy */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 pb-2 border-b border-gray-200 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-info-100 text-info-600 flex items-center justify-center text-xs font-bold">
                    1
                  </div>
                  Expense Hierarchy
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Type <span className="text-error-500">*</span>
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value, unit: "", costCenter: "", category: "" })}
                      className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                        errors.type ? "border-error-500" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Type</option>
                      {types.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    {errors.type && (
                      <p className="text-xs text-error-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.type}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Unit <span className="text-error-500">*</span>
                    </label>
                    <select
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      disabled={!formData.type}
                      className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed ${
                        errors.unit ? "border-error-500" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Unit</option>
                      {filteredUnits.map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                    {errors.unit && (
                      <p className="text-xs text-error-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.unit}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Cost Center <span className="text-error-500">*</span>
                    </label>
                    <select
                      value={formData.costCenter}
                      onChange={(e) => setFormData({ ...formData, costCenter: e.target.value, category: "" })}
                      disabled={!formData.type}
                      className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed ${
                        errors.costCenter ? "border-error-500" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Cost Center</option>
                      {filteredCostCenters.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    {errors.costCenter && (
                      <p className="text-xs text-error-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.costCenter}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Category <span className="text-error-500">*</span>
                    </label>
                    <GroupedCategoryDropdown
                      typeId={formData.type}
                      costCenterId={formData.costCenter}
                      nature={entry?.nature}
                      fallbackNature={entry?.nature || 'routine'}
                      value={formData.category}
                      onSelect={(categoryId) => setFormData({ ...formData, category: categoryId })}
                      placeholder="Select category..."
                      disabled={!formData.costCenter}
                    />
                    {errors.category && (
                      <p className="text-xs text-error-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.category}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2: Transaction Details */}
              <div className="space-y-4 pt-2">
                <h3 className="text-sm font-semibold text-gray-900 pb-2 border-b border-gray-200 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-success-100 text-success-600 flex items-center justify-center text-xs font-bold">
                    2
                  </div>
                  Transaction Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Shop/Vendor <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.shop}
                      onChange={(e) => setFormData({ ...formData, shop: e.target.value })}
                      onBlur={(e) => setFormData({ ...formData, shop: toTitleCase(e.target.value) })}
                      list="shop-list"
                      placeholder="Enter shop or vendor name"
                      className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                        errors.shop ? "border-error-500" : "border-gray-300"
                      }`}
                    />
                    <datalist id="shop-list">
                      {shopList.map(shop => (
                        <option key={shop} value={shop} />
                      ))}
                    </datalist>
                    {errors.shop && (
                      <p className="text-xs text-error-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.shop}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Date <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      max={todayStr}
                      min={minDateStr}
                      className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                        errors.date ? "border-error-500" : "border-gray-300"
                      }`}
                    />
                    {errors.date && (
                      <p className="text-xs text-error-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.date}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 3: Amount Calculation */}
              <div className="space-y-4 pt-2">
                <h3 className="text-sm font-semibold text-gray-900 pb-2 border-b border-gray-200 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-info-100 text-info-600 flex items-center justify-center text-xs font-bold">
                    3
                  </div>
                  Amount & Calculations
                </h3>
                
                {/* Input Mode Toggle */}
                <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="text-sm font-medium text-gray-700">Calculation Mode:</span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, mode: "total" })}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                        formData.mode === "total"
                          ? "bg-primary text-white shadow-sm"
                          : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                      }`}
                    >
                      Total Amount
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, mode: "unit" })}
                      disabled={!showUnits}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                        formData.mode === "unit"
                          ? "bg-primary text-white shadow-sm"
                          : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                      }`}
                    >
                      Quantity × Rate
                    </button>
                  </div>
                  {!showUnits && (
                    <span className="text-xs text-gray-500 ml-auto">Unit mode not available for this category</span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.mode === "unit" && showUnits ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Quantity <span className="text-error-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.quantity}
                          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                          placeholder="0"
                          className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                            errors.quantity ? "border-error-500" : "border-gray-300"
                          }`}
                        />
                        {errors.quantity && (
                          <p className="text-xs text-error-600 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.quantity}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Rate (per unit) <span className="text-error-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.rate}
                          onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                          placeholder="0.00"
                          className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                            errors.rate ? "border-error-500" : "border-gray-300"
                          }`}
                        />
                        {errors.rate && (
                          <p className="text-xs text-error-600 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.rate}
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Gross Amount <span className="text-error-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.gross}
                        onChange={(e) => setFormData({ ...formData, gross: e.target.value })}
                        placeholder="0.00"
                        className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                          errors.gross ? "border-error-500" : "border-gray-300"
                        }`}
                      />
                      {errors.gross && (
                        <p className="text-xs text-error-600 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.gross}
                        </p>
                      )}
                    </div>
                  )}

                  {/* GST Toggle & Input */}
                  <div className="md:col-span-2 space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={formData.enableGst}
                        onChange={(e) => setFormData({ ...formData, enableGst: e.target.checked })}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="text-sm font-medium text-gray-700">Include GST/Tax Amount</span>
                    </label>
                    
                    {formData.enableGst && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          GST/Tax Amount
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.gstAmount}
                          onChange={(e) => setFormData({ ...formData, gstAmount: e.target.value })}
                          placeholder="0.00"
                          className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 4: Additional Information */}
              <div className="space-y-4 pt-2">
                <h3 className="text-sm font-semibold text-gray-900 pb-2 border-b border-gray-200 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center text-xs font-bold">
                    4
                  </div>
                  Additional Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {showName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {selectedCategory?.name} Name <span className="text-error-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        onBlur={(e) => setFormData({ ...formData, name: toTitleCase(e.target.value) })}
                        placeholder={`Enter ${selectedCategory?.name.toLowerCase()} name`}
                        className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                          errors.name ? "border-error-500" : "border-gray-300"
                        }`}
                      />
                      {errors.name && (
                        <p className="text-xs text-error-600 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.name}
                        </p>
                      )}
                    </div>
                  )}

                  <div className={showName ? "" : "md:col-span-2"}>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Description/Notes
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      onBlur={(e) => setFormData({ ...formData, description: toTitleCase(e.target.value) })}
                      placeholder="Add any additional details..."
                      rows={3}
                      className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Net Amount Display */}
              <div className="p-4 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Final Amount</span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formData.mode === "unit" && showUnits
                        ? `${formData.quantity || 0} × ₹${formData.rate || 0}${formData.enableGst ? ` + ₹${formData.gstAmount || 0} GST` : ""}`
                        : `₹${formData.gross || 0}${formData.enableGst ? ` + ₹${formData.gstAmount || 0} GST` : ""}`
                      }
                    </p>
                  </div>
                  <span className="text-3xl font-semibold text-primary">
                    ₹{parseFloat(formData.amount || "0").toFixed(2)}
                  </span>
                </div>
              </div>

            </div>
          </div>

          {/* Footer - Sticky */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3 sticky bottom-0">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-2 min-w-[120px] justify-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : (
                <>Save Changes</>
              )}
            </Button>
          </div>

        </div>
      </div>
    </>
  );
};

export default EditEntryModal;
